import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { ArrowLeft, Plus, Edit, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import api from '../services/api';
import { toast } from 'sonner';

export const StatusesAdminPage = () => {
  const navigate = useNavigate();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [formData, setFormData] = useState({ name: '', color: '#0f172a', order: 0 });

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      const response = await api.get('/statuses');
      setStatuses(response.data);
    } catch (error) {
      toast.error('Chyba při načítání stavů');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (status = null) => {
    if (status) {
      setEditingStatus(status);
      setFormData({ name: status.name, color: status.color, order: status.order });
    } else {
      setEditingStatus(null);
      setFormData({ name: '', color: '#0f172a', order: statuses.length });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Název je povinný');
      return;
    }

    try {
      if (editingStatus) {
        await api.put(`/statuses/${editingStatus.id}`, formData);
        toast.success('Stav aktualizován');
      } else {
        await api.post('/statuses', formData);
        toast.success('Stav vytvořen');
      }
      setDialogOpen(false);
      fetchStatuses();
    } catch (error) {
      toast.error('Chyba při ukládání stavu');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Opravdu chcete smazat tento stav?')) return;

    try {
      await api.delete(`/statuses/${id}`);
      toast.success('Stav smazán');
      fetchStatuses();
    } catch (error) {
      toast.error('Chyba při mazání stavu');
    }
  };

  const predefinedColors = [
    { name: 'Červená', value: '#ef4444' },
    { name: 'Oranžová', value: '#f59e0b' },
    { name: 'Žlutá', value: '#eab308' },
    { name: 'Zelená', value: '#10b981' },
    { name: 'Modrá', value: '#3b82f6' },
    { name: 'Fialová', value: '#a855f7' },
    { name: 'Šedá', value: '#6b7280' },
    { name: 'Tmavá', value: '#0f172a' },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-slate-500">Načítání...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8" data-testid="statuses-admin-page">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zpět
          </Button>
        </div>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Stavy hlášení</h1>
            <p className="text-lg text-slate-600">Definice stavů a jejich barevné označení</p>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-status-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Přidat stav
          </Button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                  Název
                </th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                  Barva
                </th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                  Pořadí
                </th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                  Náhled
                </th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                  Akce
                </th>
              </tr>
            </thead>
            <tbody>
              {statuses.map((status) => (
                <tr
                  key={status.id}
                  className="border-b border-slate-100 transition-colors hover:bg-slate-50/50"
                  data-testid={`status-row-${status.id}`}
                >
                  <td className="p-4 align-middle font-medium text-slate-900">
                    {status.name}
                  </td>
                  <td className="p-4 align-middle text-sm text-slate-700">
                    {status.color}
                  </td>
                  <td className="p-4 align-middle text-sm text-slate-700">
                    {status.order}
                  </td>
                  <td className="p-4 align-middle">
                    <div
                      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
                      style={{ backgroundColor: status.color + '20', color: status.color }}
                    >
                      {status.name}
                    </div>
                  </td>
                  <td className="p-4 align-middle">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleOpenDialog(status)}
                        data-testid={`edit-status-${status.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(status.id)}
                        data-testid={`delete-status-${status.id}`}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent data-testid="status-dialog">
            <DialogHeader>
              <DialogTitle>
                {editingStatus ? 'Upravit stav' : 'Nový stav'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Název *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Název stavu"
                  data-testid="status-name-input"
                />
              </div>
              <div>
                <Label htmlFor="order">Pořadí</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  data-testid="status-order-input"
                />
              </div>
              <div>
                <Label>Barva *</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {predefinedColors.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: color.value })}
                      className={`h-10 rounded-md border-2 transition-all ${
                        formData.color === color.value ? 'border-slate-900 scale-105' : 'border-slate-200'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                      data-testid={`color-${color.value}`}
                    />
                  ))}
                </div>
                <div className="mt-3">
                  <Label htmlFor="custom-color" className="text-xs">Vlastní barva (hex)</Label>
                  <Input
                    id="custom-color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="#000000"
                    data-testid="status-color-input"
                  />
                </div>
                <div className="mt-3">
                  <Label className="text-xs">Náhled</Label>
                  <div
                    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold mt-1"
                    style={{ backgroundColor: formData.color + '20', color: formData.color }}
                    data-testid="status-preview"
                  >
                    {formData.name || 'Náhled'}
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                data-testid="cancel-dialog-button"
              >
                Zrušit
              </Button>
              <Button
                onClick={handleSave}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="save-status-button"
              >
                Uložit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
